import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { I18nextProvider } from "react-i18next";

import { ReactComponent as TextIcon } from "../../../../../assets/dashboard/link/textIcon.svg";
import { ReactComponent as TextIconDark } from "../../../../../assets/MainPage/addBlock/countDown/text-dark.svg";

import { ReactComponent as LinkIcon } from "../../../../../assets/dashboard/link/linkIcon.svg";
import { ReactComponent as LinkIconDark } from "../../../../../assets/dashboard/link/linkIcon-dark.svg";
import { ReactComponent as GalleryIcon } from "../../../../../assets/dashboard/link/galleryIcon.svg";
import { ReactComponent as TrashIcon } from "../../../../../assets/dashboard/link/trashIcon.svg";
import { ReactComponent as TrashIconDark } from "../../../../../assets/dashboard/link/trashIcon-dark.svg";

import { IoMdCloseCircle } from "react-icons/io";
import ExtraSmallFilledButton from "../../../../../components/common/buttons/ExtraSmallFilledButton";
import useLocales from "../../../../../hooks/useLocales";
import { useSelector } from "react-redux";
import { ReactComponent as DownArrow } from "../../../../../assets/settingPage/arrow-down.svg";
import i18nForComponent from "../../../../../locales/component/i18nForComponent";

const LinkForm = ({
  formType,
  deleteHandler,
  submitHandlerParent,
  isLoading,
  prevData,
}) => {
  const { darkMode } = useSelector((state) => state.theme);
  const { allLangs, tComponent, handleLanguageChange } =
    useLocales();

  const baseUrl = process.env.REACT_APP_API_URL;
  const [filePreview, setFilePreview] = useState(null);

  const [selectReporterTypeDropDown, setSelectReporterTypeDropDown] =
    useState(false);
  const [selectedReporterType, setSelectedReporterType] = useState("English");
  const [selectedLang, setSelectedLang] = useState("en");

  const schema = yup.object().shape({
    coloumnType: yup.string().required(tComponent("link.link_is_required")),
    coloumnValue: yup
      .string()
      .test("required", tComponent("link.link_url_required"), (val) => !!val)
      .required(tComponent("link.link_url_required")),
    linkIcon: null,
  });
  useEffect(() => {
    if (prevData?.coloum_value) {
      if (prevData?.linkIcon) {
        setFilePreview(baseUrl + "/profile/" + prevData?.linkIcon);
      }
      setValue("coloumnType", prevData?.coloum_type);
      setValue(
        "coloumnValue",
        prevData?.coloum_value &&
          JSON.parse(prevData?.coloum_value)?.coloum_value
      );
      if (prevData?.language) {
        setSelectedLang(prevData?.language);
        const matchedLang = allLangs?.find(
          (lang) => lang.value === prevData?.language
        );
        const shouldSetLang = matchedLang?.label;
        setSelectedReporterType(shouldSetLang);
        handleLanguageChange(prevData?.language);
      }
    }
  }, [prevData]);

  useEffect(() => {
    if (selectedLang) {
      handleLanguageChange(selectedLang);
    }
  }, [selectedLang]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      coloumnType: "",
      coloumnValue: "",
      linkIcon: null,
      language: "en",
    },
  });

  const submitHandler = async (data) => {
    if (filePreview && prevData?.linkIcon) {
      data.prevIcon = prevData?.linkIcon;
    }
    data.language = selectedLang;
    submitHandlerParent(data);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setValue("linkIcon", file);
    setFilePreview(URL.createObjectURL(file));
  };

  const handleChangeLang = (newLang) => {
    handleLanguageChange(newLang);
  };

  return (
    <I18nextProvider i18n={i18nForComponent}>
      <div className="sm:pt-[30px] pt-10 md:px-0 px-6">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div
            className="border border-light_bg_v1 rounded-[10px] bg-light_bg_v5 dark:bg-dark-secondary p-6 sm:p-[25px] dark:border-color_dark"
            style={{
              direction: selectedLang === "ar" ? "rtl" : "ltr",
            }}
          >
            <div className="flex justify-between items-center mb-[20px] sm:mb-[25px]">
              <p className="text-small2_b sm:text-small1_bold text-heading_color dark:text-color_v1_dark uppercase">
                {formType === "edit" && tComponent(`link.${formType}`)}
                {formType === "add" && tComponent(`link.${formType}`)}
                &nbsp;
                {tComponent("link.link_cap")}
              </p>
              {formType === "add" && (
                <div className="flex items-center justify-center">
                  <div className="relative w-[115px] sm:w-[145px]">
                    <div
                      onClick={() => {
                        setSelectReporterTypeDropDown(
                          !selectReporterTypeDropDown
                        );
                      }}
                      className={`bg-light_bg_v4 capitalize p-[13px] text-small2 lg:text-body_copy2 rounded-md w-full flex justify-between items-center cursor-pointer`}
                    >
                      {selectedReporterType
                        ? selectedReporterType
                        : tComponent("link.select_lang")}
                      <DownArrow />
                    </div>
                    {selectReporterTypeDropDown && (
                      <div className="absolute top-14 mb-20 left-0 w-full z-50 p-[7px] px-[10px] md:p-[15px] bg-light_bg_v5  rounded-lg border flex flex-col max-h-40 overflow-y-auto">
                        {allLangs?.map((item, i) => {
                          return (
                            <div
                              key={i}
                              onClick={() => {
                                setSelectedLang(item.value); // Set selected language code
                                setSelectedReporterType(item?.label);
                                setSelectReporterTypeDropDown(false);
                                handleChangeLang(item?.value);
                              }}
                              className="px-[10px] md:px-[15px] py-[7px] md:py-[10px] capitalize text-small2 lg:text-body_copy2 text-heading_color cursor-pointer hover:bg-light_bg_v4 rounded-md"
                            >
                              {item.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {formType === "edit" && (
                <div
                  className={`w-[100px] bg-light_bg_v4 capitalize p-[10px] text-small2 lg:text-body_copy2 rounded-md flex justify-center items-center`}
                >
                  {selectedReporterType ? selectedReporterType : "English"}
                </div>
              )}
            </div>
            <div className="relative">
              <div className="absolute z-[9] text-heading_color h-full start-0 flex items-center inset-y-0 px-3 p-[7px]">
                {darkMode ? <TextIconDark /> : <TextIcon />}
              </div>
              <input
                type="text"
                id="coloumnType"
                {...register("coloumnType")}
                className={`ps-[36px] bg-light_bg_v5 border text-heading_color text-small1 rounded-lg focus:outline-none block w-full p-[11px] py-[3px] px-[15px] dark:text-color_v1_dark placeholder:dark:text-font_light_color dark:caret-white focus:placeholder:dark:text-color_v1_dark dark:bg-dark-secondary ${
                  errors.coloumnType
                    ? "border-state_color_error dark:bg-dark-secondary"
                    : "border-light_bg_v2 bg-transparent focus:border-font_light_color focus:placeholder:text-heading_color dark:border-color_dark focus:dark:border-font_color"
                }`}
                placeholder={tComponent("link.enter_link_title_en")}
              />
            </div>
            {errors.coloumnType && (
              <small className="text-state_color_error text-small3">
                {errors.coloumnType.message}
              </small>
            )}
            <div className="relative mt-2.5">
              <div className="absolute z-[9] text-heading_color h-full start-0 flex items-center inset-y-0 px-3 p-[7px]">
                {darkMode ? <LinkIconDark /> : <LinkIcon />}
              </div>
              <input
                type="text"
                id="coloumnValue"
                {...register("coloumnValue")}
                className={`ps-[36px] bg-light_bg_v5 border text-heading_color text-small1 rounded-lg focus:outline-none block w-full p-[11px] py-[3px] px-[15px] dark:text-color_v1_dark placeholder:dark:text-font_light_color dark:caret-white focus:placeholder:dark:text-color_v1_dark dark:bg-dark-secondary ${
                  errors.coloumnValue
                    ? "border-state_color_error dark:bg-dark-secondary"
                    : "border-light_bg_v2 bg-transparent focus:border-font_light_color focus:placeholder:text-heading_color dark:border-color_dark focus:dark:border-font_color"
                }`}
                placeholder={tComponent("link.enter_link") + " URL"}
              />
            </div>
            {errors.coloumnValue && (
              <small className="text-state_color_error text-small3">
                {errors.coloumnValue.message}
              </small>
            )}
            <div className="mt-4">
              {filePreview ? (
                <div className="flex items-center">
                  <div className="bg-light_bg_v4 rounded-lg cursor-pointer relative">
                    <div
                      onClick={() => {
                        setFilePreview(null);
                        setValue("linkIcon", null);
                      }}
                      className="absolute cursor-pointer -right-2 -top-2 text-state_color_error text-lg"
                    >
                      <IoMdCloseCircle />
                    </div>
                    <img
                      className="h-[60px] w-[60px] sm:h-[64px] sm:w-[64px] p-1 object-cover rounded-lg"
                      src={filePreview}
                      alt="..."
                    />
                  </div>
                </div>
              ) : (
                <label htmlFor="dropzone-file">
                  <div className="flex items-center">
                    <div className="bg-[#D9D9D9] dark:bg-dark-primary rounded-lg p-[12.5px] cursor-pointer dark:border-color_v2_dark">
                      <GalleryIcon />
                    </div>
                    <div className="ps-3 leading-none text-small3_b text-main_color cursor-pointer">
                      {tComponent("link.add_icon")}
                    </div>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {errors.linkIcon && (
                    <small className="text-state_color_error text-small3">
                      {errors.linkIcon.message}
                    </small>
                  )}
                </label>
              )}
            </div>
            <div className="flex mt-[26px]  items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  deleteHandler();
                }}
                className="flex cursor-pointer justify-center items-center"
              >
                {darkMode ? <TrashIconDark /> : <TrashIcon />}
                <p className="ps-1.5 pt-1 text-small3_b gray-700 text-heading_color dark:text-color_v1_dark">
                  {tComponent("link.delete_cap")}
                </p>
              </button>
              <ExtraSmallFilledButton
                buttonType={"submit"}
                isLoading={isLoading}
                name={`${tComponent("link.save_cap")}`}
                classes={"ms-3"}
              />
            </div>
          </div>
        </form>
      </div>
    </I18nextProvider>
  );
};

export default LinkForm;
